import React from "react";
import { Drawer } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { EmployeeDetails } from "../../../serviceoperation/entity/EmployeeOperationEntity";
import { useLocations } from "../helpers/useLocations";
import DrawerActions from "./DrawerActions";
import EmployeeHeaderCard from "./EmployeeHeaderCard";
import EmployeeInvitationNotice from "./EmployeeInvitationNotice";
import EmployeeTabs from "./EmployeeTabs";
import { useEmployeeForm } from "../helpers/useEmployeeForm";

interface Props {
  open: boolean;
  employee: EmployeeDetails | null;
  onClose: () => void;
  onUpdate: (employee: EmployeeDetails) => void;
  onDelete: (employeeId: string) => void;
  onResendInvite?: (employeeId: string, email: string) => Promise<void>;
}

const EmployeeDetailsDrawer: React.FC<Props> = ({
  open,
  employee,
  onClose,
  onUpdate,
  onDelete,
  onResendInvite,
}) => {
  const {
    form,
    isEditing,
    isSaving,
    resendingInvite,
    setIsEditing,
    handleCancel,
    handleSave,
    handleDelete,
    handleResendInvite,
    initialValues,
  } = useEmployeeForm({
    employee,
    onClose,
    onUpdate,
    onDelete,
    onResendInvite,
  });

  const { locations } = useLocations();

  if (!employee) return null;

  return (
    <Drawer
      title={isEditing ? "Edit Employee" : "Employee Details"}
      placement="right"
      open={open}
      onClose={handleCancel}
      width={900}
      closeIcon={<CloseOutlined />}
      destroyOnClose
    >
      <DrawerActions
        isEditing={isEditing}
        isSaving={isSaving}
        resendingInvite={resendingInvite}
        setIsEditing={setIsEditing}
        handleSave={handleSave}
        handleCancel={handleCancel}
        handleDelete={handleDelete}
        employee={employee}
        handleResendInvite={handleResendInvite}
      />

      <div style={{ padding: 24 }}>
        <EmployeeHeaderCard employee={employee} locations={locations} />

        {employee.invitePending && (
          <EmployeeInvitationNotice
            resendingInvite={resendingInvite}
            onResendInvite={handleResendInvite}
          />
        )}

        {/* Forms + Tabs */}
        <EmployeeTabs
          form={form}
          initialValues={initialValues}
          isEditing={isEditing}
          locations={locations}
          employee={employee}
        />
      </div>
    </Drawer>
  );
};

export default EmployeeDetailsDrawer;
